import {
    Grid,
    makeStyles,
    Theme,
    RadioGroup,
    Radio,
    FormControlLabel,
    Typography,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core'
import { store } from '../../index'
import DashboardClient from 'clients/dashboard-client'
import { InvoicePeriodStatistic } from 'models/invoice-period-statistic'
import React, { useEffect, useState } from 'react'
import { tr } from 'utils/translations/translate'
import { BillingDialog } from '../contract-page/billing-dialog'
import { BillingConfiguration } from 'models/billing-configuration'
import { InvoicesSendResult } from 'models/invoices-send-result'
import { actionCubitSnackbarShow } from '../../redux/cubit-snackbar/cubit-snackbar-actions'
import { Period } from './period'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/app/app-store'
import {
    actionSetLoadingInProgress,
    actionUpdatePeriodStatistics,
    actionUpdatePeriodStatisticsSuccess,
} from '../../redux/dashboard/period-statistics-actions'
import { actionLoadInvoiceFiles } from '../../redux/invoice-file/invoice-file-table-actions'
import {
    TEXT_DIALOG_CONTINUE_QUESTION,
    TEXT_DIFF_FILE_SELECTION_TITLE,
    TEXT_DIFF_FILE_TERM_FROM,
    TEXT_DIFF_FILE_TERM_TO,
    TEXT_FROM_TERM_MUST_BE_SMALLER,
    TEXT_GENERATE_DIFF_FILE,
    TEXT_GROUPING_TYPE_BY_PRODUCT,
    TEXT_GROUP_BY_BUILDING_NUMBER,
    TEXT_GROUP_BY_MATRIKKEL_NUMBER,
    TEXT_OK_RESET_PERIOD,
    TEXT_RESET_INVOICES_DIALOG_TEXT,
    TEXT_RESET_PERIOD,
    TEXT_SEND_TO_ERP,
    TEXT_TERMIN,
    TEXT_TERM_WAS_RESET_SUCCESSFULLY,
    TEXT_TO_TERM_MUST_BE_GREATER,
    TEXT_YES,
} from 'utils/translations/keys'
import {
    GROUPING_TYPE_BY_BUILDING,
    GROUPING_TYPE_BY_PRODUCT,
    GROUPING_TYPE_NONE,
    PERIODIC_TYPE_RUNNING,
} from './dashboard-constants'
import { useUser } from '../../redux/auth/auth-selectors'
import { InvoicingSystemType } from 'models/tenant-config'
import { SendToErpDialog } from './send-to-erp-dialog'
import ErrorIcon from '@material-ui/icons/Error'
import clsx from 'clsx'
import { ErpTransferStatisticLine } from 'models/erp-transfer-statistic'
import { setSendToErpRunning } from '../../redux/dashboard/erp-statistic-actions'
import saveAs from 'file-saver'

const useStyles = makeStyles((theme: Theme) => {
    return {
        container: {
            margin: '8px',
        },
        header: {
            padding: '0 62px 0 24px',
        },
        detailsHeader: {
            paddingRight: '38px',
        },
        numericColumn: {
            textAlign: 'right',
        },
        expansionPanel: {
            borderRadius: '0!important',
            boxShadow: 'none',
            background: 'transparent',
            margin: '6px 0 0 0',
            '&::before': {
                display: 'none',
            },
            '&.Mui-expanded': {
                margin: '6px 0 0 0',
            },
            '& > .MuiExpansionPanelSummary-root': {
                background: 'white',
                '&.Mui-expanded': {
                    minHeight: 'initial',
                    margin: 'initial',
                },
                '& > .MuiExpansionPanelSummary-content.Mui-expanded': {
                    margin: 0,
                },
            },
            '& > .MuiCollapse-container': {
                marginTop: '2px',
                background: 'white',
            },
        },
        tableRow: {
            '&:last-child td': {
                borderBottom: 0,
            },
            '&:hover': {
                cursor: 'pointer',
            },
        },
        sendToErp: {
            marginTop: '1rem',
        },
        sendToErpContainer: {
            paddingTop: '2rem',
        },
        subtotals: {
            borderTop: '1px #f0f0f0 solid',
            paddingTop: '0.25rem',
            marginTop: '0.25rem',
        },
        periodSelectionLabel: {
            fontSize: 14,
        },
        periodSelect: {
            maxWidth: 160,
        },
        errorIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
        },
        errorDescription: {
            paddingLeft: 10,
            fontSize: 14,
            color: 'rgba(0, 0, 0, 0.54)',
        },
        paddingTop: {
            paddingTop: 13,
        },
        containerMargins: {
            marginBottom: 20,
            marginTop: 20,
        },
        iconContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        resetTermDialogText:{ 
            marginTop: 20
        }
    }
})

const defaultProduct = {
    id: 'All',
    name: 'All',
    description: '',
    price: 0,
    type: { label: '', value: 0 },
    validFrom: '',
    validTo: '',
    vatCode: '',
    vatPercentage: 0,
    status: { label: '', value: 0 },
    productId: 0,
    vatCodeId: '',
    productBillingType: '',
    periodType: '',
    selectedPeriods: [0],
    needsInvoicesUpdate: false,
    priceType: 0,
    otherPriceTypeValue: '',
    perTimePriceTypeValue: 0,
}
const defaultSelectedPeriod: InvoicePeriodStatistic = { period: 0, year: 0, products: [] }
const defaultInvoicesSendResult: InvoicesSendResult = { failureCount: 0, successCount: 0, problemCategories: {} }

export const PeriodAccordions: React.FC<{
    periodStatistics: InvoicePeriodStatistic[]
    billingConfiguration?: BillingConfiguration
    erpTransferStatistics: ErpTransferStatisticLine[]
}> = (props) => {
    const dispatch = useDispatch()

    const [numberOfInvoicesToSend, setNumberOfInvoicesToSend] = useState(0)
    const [inputNumberOfInvoicesToSend, setInputNumberOfInvoicesToSend] = useState('')
    const [sendToErpDialogOpen, setSendToErpDialogOpen] = useState(false)
    const [invoicesSendResult, setInvoicesSendResult] = useState<InvoicesSendResult>(defaultInvoicesSendResult)
    const [selectedPeriodForSending, setSelectedPeriodForSending] =
        useState<InvoicePeriodStatistic>(defaultSelectedPeriod)
    const isUpdatingStatistics = useSelector((state: AppState) => state.invoicePeriodStatistics.isUpdatingStatistics)
    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
    const [invoiceType, setInvoiceType] = useState(0)
    const [periodicCostType, setPeriodicCostType] = useState(PERIODIC_TYPE_RUNNING)
    const [groupingType, setGroupingType] = useState(GROUPING_TYPE_BY_PRODUCT)
    const [diffFileGroupingType, setDiffFileGroupingType] = useState(GROUPING_TYPE_NONE)
    const [generateDiffFileDialogOpen, setGenerateDiffFileDialogOpen] = useState(false)
    const user = useUser()
    const styles = useStyles()
    const isVisma = tenantConfig?.invoiceSystem.toString() === InvoicingSystemType[InvoicingSystemType.Visma]
    const isKomtek = tenantConfig?.invoiceSystem.toString() === InvoicingSystemType[InvoicingSystemType.KomTekFile]
    const isTripletex = tenantConfig?.invoiceSystem.toString() === InvoicingSystemType[InvoicingSystemType.Tripletex]
    const isPoweroffice =
        tenantConfig?.invoiceSystem.toString() === InvoicingSystemType[InvoicingSystemType.PowerOffice]

    const periodConfig = useSelector((state: AppState) => state.config.periodConfigurations)
    const sendToErpRunning = useSelector((state: AppState) => state.erpTransferStatisticState.sendToErpRunning)
    const [invoiceSummaryDialogOpen, setInvoiceSummaryDialogOpen] = useState(false)
    const [selectedInvoiceSummaryPeriod, setInvoiceSummaryPeriod] = useState<InvoicePeriodStatistic>()
    const [selectedInvoiceSummaryProduct, setInvoiceSummaryProduct] = useState<string>(defaultProduct.id)
    const products = useSelector((state: AppState) => state.products.items)
    const [resetPeriodOpen, setResetPeriodOpen] = useState(false)

    const isApiBasedInvoicing = isVisma || isTripletex || isPoweroffice

    const openInvoiceSummaryDialog = (period) => {
        setInvoiceSummaryDialogOpen(true)
        setInvoiceSummaryPeriod(period)
    }

    const generateDiffFile = async () => {
        if (!selectedPeriodForSending) return

        dispatch(setSendToErpRunning(true))
        setGenerateDiffFileDialogOpen(false)
        setIsLoading(true)
        const selectedDiffDateFromObj = JSON.parse(selectedDiffDateFrom)
        await DashboardClient.generateDiffFile(
            selectedDiffDateFromObj.year,
            selectedDiffDateFromObj.period,
            selectedPeriodForSending.year,
            selectedPeriodForSending.period,
            diffFileGroupingType,
        ).toPromise()
        setIsLoading(false)
        dispatch(setSendToErpRunning(false))
        dispatch(actionLoadInvoiceFiles())
    }

    const resetPeriod = async () => {
        if (!selectedPeriodForSending) return

        const period = selectedPeriodForSending
        setResetPeriodOpen(false)
        dispatch(setSendToErpRunning(true))
        setIsLoading(true)
        await DashboardClient.resetPeriod(period.year, period.period).toPromise()
        const statistics = await DashboardClient.refreshPeriodStatistics(period.year, period.period).toPromise()
        dispatch(actionUpdatePeriodStatisticsSuccess(statistics))
        setIsLoading(false)
        dispatch(setSendToErpRunning(false))
        dispatch(actionCubitSnackbarShow([tr(TEXT_TERM_WAS_RESET_SUCCESSFULLY)], 'success'))
    }

    const handleResetPeriod = (period) => {
        setResetPeriodOpen(true)
        setSelectedPeriodForSending(period)
    }

    const generateInvoices = async (period: InvoicePeriodStatistic) => {
        setIsLoading(true)
        await DashboardClient.generateInvoices(period.year, period.period).toPromise()
        setIsLoading(false)
    }

    const handleGenerateDiffFile = (period) => {
        setGenerateDiffFileDialogOpen(true)
        setSelectedPeriodForSending(period)
    }

    const handleGenereateDiffFileClose = () => {
        setGenerateDiffFileDialogOpen(false)
    }

    const sendToErp = async () => {
        if (selectedPeriodForSending === null) {
            return
        }

        const numberOfInvoices =
            inputNumberOfInvoicesToSend.length > 0 ? parseInt(inputNumberOfInvoicesToSend) : undefined
        if (numberOfInvoices !== undefined && numberOfInvoices <= 0) {
            store.dispatch(actionCubitSnackbarShow(['Number of input should be more than 0'], 'warning'))
            return
        }

        if (isApiBasedInvoicing) {
            setSendToErpDialogOpen(false)
        }

        dispatch(setSendToErpRunning(true))

        const result = await DashboardClient.sendPeriodToErp(
            selectedPeriodForSending.year,
            selectedPeriodForSending.period,
            invoiceType,
            periodicCostType,
            groupingType,
            numberOfInvoices,
        ).toPromise()

        dispatch(setSendToErpRunning(false))

        setInvoicesSendResult(result && !isApiBasedInvoicing ? result : defaultInvoicesSendResult)

        if (result) {
            updateStatistic(selectedPeriodForSending)
            dispatch(actionLoadInvoiceFiles())

            if (invoicesSendResult.failureCount === 0) {
                handleSendToErpDialogClose()
            }
        } else {
            setSendToErpDialogOpen(false)
        }
    }

    const setIsLoading = (isInProgress: boolean) => {
        dispatch(actionSetLoadingInProgress(isInProgress))
    }

    const updateStatistic = (statistic: InvoicePeriodStatistic) => {
        dispatch(actionUpdatePeriodStatistics(statistic.year, statistic.period))
    }

    const handleSendToErpDialogOpen = (period: InvoicePeriodStatistic) => {
        setSelectedPeriodForSending(period)
        setSendToErpDialogOpen(true)

        setNumberOfInvoicesToSend(
            !props.billingConfiguration?.isResendEnabled
                ? (period.paymentInvoicesNotTransferred ?? 0)
                : (period.paymentInvoiceCount ?? 0),
        )

        setInputNumberOfInvoicesToSend('')
    }

    const handleSendToErpDialogClose = () => {
        setSelectedPeriodForSending(defaultSelectedPeriod)
        setSendToErpDialogOpen(false)
        setNumberOfInvoicesToSend(0)
        setInvoicesSendResult(defaultInvoicesSendResult)
        setInputNumberOfInvoicesToSend('')
    }

    const onInvoiceTypeChange = (value) => {
        setInvoiceType(value)
    }

    const handleChangePeriodicType = (value) => {
        setPeriodicCostType(parseInt(value))
    }

    const handleChangeGroupingType = (value) => {
        setGroupingType(parseInt(value))
    }

    const onDateFromChange = (value) => {
        setSelectedDiffDateFrom(value)
    }

    const onDateToChange = (value) => {
        setSelectedDiffDateTo(value)
    }

    const getPrevPeriod = () => {
        if (selectedPeriodForSending.period === 1 && periodConfig) {
            const prevYearConfig = periodConfig.find((x) => x.year === selectedPeriodForSending.year - 1)

            if (prevYearConfig) {
                return {
                    period: prevYearConfig.periods[prevYearConfig.periods.length - 1].periodNumber,
                    year: prevYearConfig.year,
                }
            }
            return { period: selectedPeriodForSending.period, year: selectedPeriodForSending.year }
        }
        return { period: selectedPeriodForSending.period - 1, year: selectedPeriodForSending.year }
    }

    useEffect(() => {
        setSelectedDiffDateFrom(JSON.stringify(getPrevPeriod()))
        setSelectedDiffDateTo(
            JSON.stringify({ period: selectedPeriodForSending.period, year: selectedPeriodForSending.year }),
        )
    }, [selectedPeriodForSending])

    const [selectedDiffDateFrom, setSelectedDiffDateFrom] = useState(JSON.stringify(getPrevPeriod()))
    const [selectedDiffDateTo, setSelectedDiffDateTo] = useState(
        JSON.stringify({ period: selectedPeriodForSending.period, year: selectedPeriodForSending.year }),
    )

    const hasValidPeriodSelection = () => {
        const fromPeriod = JSON.parse(selectedDiffDateFrom)
        const toPeriod = JSON.parse(selectedDiffDateTo)

        if (fromPeriod.year === toPeriod.year) {
            return toPeriod.period >= fromPeriod.period
        }
        return toPeriod.year > fromPeriod.year
    }

    const downloadInvoiceSummary = async (statistic: InvoicePeriodStatistic | undefined, productId: string) => {
        if (!statistic) return
        setIsLoading(true)
        const fileResult = await DashboardClient.getInvoiceSummary(statistic.year, statistic.period, productId)
        saveAs(fileResult, `${statistic.year}-${statistic.period}_invoice_summary.xlsx`)
        setIsLoading(false)
    }

    const handleInvoiceSummaryDownload = () => {
        setInvoiceSummaryDialogOpen(false)
        downloadInvoiceSummary(
            selectedInvoiceSummaryPeriod,
            selectedInvoiceSummaryProduct === 'All' ? '' : selectedInvoiceSummaryProduct,
        )
    }

    const handleCloseInvoiceSummary = () => {
        setInvoiceSummaryDialogOpen(false)
        setInvoiceSummaryPeriod(undefined)
        setInvoiceSummaryProduct(defaultProduct.id)
    }

    const onProductChange = (product) => {
        setInvoiceSummaryProduct(product)
    }

    return (
        <div className={styles.container}>
            <Grid container className={styles.header}>
                <Grid item xs={1}>
                    <strong>{tr('TEXT_TERMIN')}</strong>
                </Grid>
                <Grid item xs={1}>
                    <strong>{tr('TEXT_TERMIN_START')}</strong>
                </Grid>
                <Grid item xs={2}>
                    <strong>{tr('TEXT_TERMIN_END')}</strong>
                </Grid>
                <Grid item xs={2} className={styles.numericColumn}>
                    <strong>{tr('TEXT_DOCUMENTS_NUMBER')}</strong>
                </Grid>
                <Grid item xs={2} className={styles.numericColumn}>
                    <strong>{tr('TEXT_NETAMOUNT')}</strong>
                </Grid>
                <Grid item xs={2} className={styles.numericColumn}>
                    <strong>{tr('TEXT_VATAMOUNT')}</strong>
                </Grid>
                <Grid item xs={2} className={styles.numericColumn}>
                    <strong>{tr('TEXT_AMOUNT')}</strong>
                </Grid>
            </Grid>
            {props.periodStatistics
                .filter((x) => !!x.startDate)
                .map((period, index) => (
                    <Period
                        key={index}
                        period={period}
                        index={index}
                        handleSendToErpDialogOpen={handleSendToErpDialogOpen}
                        setIsLoading={setIsLoading}
                        isResendEnabled={props.billingConfiguration?.isResendEnabled}
                        refreshStatistics={updateStatistic}
                        isLoading={isUpdatingStatistics}
                        generateDiffFile={() => handleGenerateDiffFile(period)}
                        showGenerateError={isApiBasedInvoicing}
                        showErpTransferStatistic={isApiBasedInvoicing}
                        erpTransferStatistic={props.erpTransferStatistics}
                        isErpTransferRunning={sendToErpRunning}
                        openInvoiceSummaryDialog={openInvoiceSummaryDialog}
                        resetPeriod={() => handleResetPeriod(period)}
                        generateInvoices={generateInvoices}
                    ></Period>
                ))}
            <BillingDialog
                title={tr(TEXT_GENERATE_DIFF_FILE)}
                open={generateDiffFileDialogOpen}
                handleClose={handleGenereateDiffFileClose}
                handleOk={() => generateDiffFile()}
                okText={tr(TEXT_GENERATE_DIFF_FILE)}
                showOk={hasValidPeriodSelection()}
            >
                <Grid container>
                    <Grid item xs={12}>
                        <Typography>{tr(TEXT_GROUPING_TYPE_BY_PRODUCT)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <RadioGroup
                            aria-label="grouping type"
                            name="groupingType"
                            onChange={(e) => setDiffFileGroupingType(parseInt(e.target.value))}
                            value={diffFileGroupingType}
                        >
                            <FormControlLabel
                                value={GROUPING_TYPE_NONE}
                                control={<Radio />}
                                label={tr(TEXT_GROUP_BY_MATRIKKEL_NUMBER)}
                            />
                            <FormControlLabel
                                value={GROUPING_TYPE_BY_BUILDING}
                                control={<Radio />}
                                label={tr(TEXT_GROUP_BY_BUILDING_NUMBER)}
                            />
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={12} className={styles.containerMargins}>
                        <Typography>{tr(TEXT_DIFF_FILE_SELECTION_TITLE)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <InputLabel id="select-date-from-input-label" className={styles.periodSelectionLabel}>
                            {tr(TEXT_DIFF_FILE_TERM_FROM)}
                        </InputLabel>
                        <Select
                            className={styles.periodSelect}
                            labelId="select-date-from-input-label"
                            onChange={(e) => onDateFromChange(e.target.value)}
                            value={selectedDiffDateFrom}
                        >
                            {periodConfig?.map((x) => {
                                return x.periods.map((y) => (
                                    <MenuItem
                                        key={`${x.year}-${y.periodNumber}`}
                                        value={JSON.stringify({
                                            period: y.periodNumber,
                                            year: x.year,
                                        })}
                                    >
                                        {tr(TEXT_TERMIN)} {y.periodNumber} {x.year}
                                    </MenuItem>
                                ))
                            })}
                        </Select>
                    </Grid>
                    <Grid item xs={6}>
                        <InputLabel id="select-date-to-input-label" className={styles.periodSelectionLabel}>
                            {tr(TEXT_DIFF_FILE_TERM_TO)}
                        </InputLabel>
                        <Select
                            className={styles.periodSelect}
                            labelId="select-date-to-input-label"
                            onChange={(e) => onDateToChange(e.target.value)}
                            value={selectedDiffDateTo}
                        >
                            {periodConfig?.map((x) => {
                                return x.periods.map((y) => (
                                    <MenuItem
                                        key={`${x.year}-${y.periodNumber}`}
                                        value={JSON.stringify({
                                            period: y.periodNumber,
                                            year: x.year,
                                        })}
                                    >
                                        {tr(TEXT_TERMIN)} {y.periodNumber} {x.year}
                                    </MenuItem>
                                ))
                            })}
                        </Select>
                    </Grid>
                    <Grid item xs={1} className={clsx(styles.containerMargins, styles.iconContainer)}>
                        <ErrorIcon className={styles.errorIcon}></ErrorIcon>
                    </Grid>
                    <Grid item xs={11} className={styles.containerMargins}>
                        <Typography className={clsx(styles.errorDescription, styles.paddingTop)}>
                            {tr(TEXT_FROM_TERM_MUST_BE_SMALLER)}
                        </Typography>
                        <Typography className={styles.errorDescription}>{tr(TEXT_TO_TERM_MUST_BE_GREATER)}</Typography>
                    </Grid>
                </Grid>
            </BillingDialog>
            <SendToErpDialog
                groupingType={groupingType}
                handleChangeGroupingType={handleChangeGroupingType}
                handleChangePeriodicType={handleChangePeriodicType}
                handleSendToErpDialogClose={handleSendToErpDialogClose}
                inputNumberOfPaymentInvoicesToSend={inputNumberOfInvoicesToSend}
                invoiceType={invoiceType}
                invoicesSendResult={invoicesSendResult}
                showKomtekSettings={isKomtek}
                sendToErpDialogOpen={sendToErpDialogOpen}
                sendToErpRunning={sendToErpRunning}
                numberOfInvoicesToSend={numberOfInvoicesToSend}
                onInvoiceTypeChange={onInvoiceTypeChange}
                periodicCostType={periodicCostType}
                sendToErp={sendToErp}
                setInputNumberOfInvoicesToSend={setInputNumberOfInvoicesToSend}
                styles={styles}
                user={user}
                selectedPeriodForSending={selectedPeriodForSending}
            ></SendToErpDialog>
            <BillingDialog
                title={tr('TEXT_DOWNLOAD_INVOICE_SUMMARY')}
                open={invoiceSummaryDialogOpen}
                handleClose={handleCloseInvoiceSummary}
                handleOk={handleInvoiceSummaryDownload}
                okText={tr('TEXT_DOWNLOAD_INVOICE_SUMMARY')}
                showOk={true}
            >
                <Grid container>
                    <Grid item xs={12}>
                        <InputLabel id="select-product-input-label">{tr('TEXT_SELECT_PRODUCT')}:</InputLabel>
                        <Select
                            labelId="select-product-input-label"
                            onChange={(e) => onProductChange(e.target.value)}
                            value={selectedInvoiceSummaryProduct}
                        >
                            {[defaultProduct, ...products].map((x, i) => (
                                <MenuItem key={x.id} value={x.id}>
                                    {x.name} {i > 0 && x.productId}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                </Grid>
            </BillingDialog>
            <BillingDialog
                title={tr(TEXT_RESET_PERIOD)}
                open={resetPeriodOpen}
                handleClose={() => setResetPeriodOpen(false)}
                handleOk={() => resetPeriod()}
                okText={tr(TEXT_OK_RESET_PERIOD)}
                showOk={true}
            >
                <Typography>
                    {tr(TEXT_RESET_INVOICES_DIALOG_TEXT)}
                </Typography>
                <Typography className={styles.resetTermDialogText}>{tr(TEXT_DIALOG_CONTINUE_QUESTION)}</Typography>
            </BillingDialog>
        </div>
    )
}
